import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../components/AuthContext';
import api from '../../api/client';
import toast from 'react-hot-toast';
import {
  UserPlus, Shield, ToggleLeft, ToggleRight, Clock,
  CheckCircle, XCircle, Mail, Search, RefreshCw, AlertTriangle
} from 'lucide-react';

const formatDate = (d) => d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—';

export default function AdminManagement() {
  const { user: currentUser } = useAuth();
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', email: '' });
  const [creating, setCreating] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get('/admin/admins');
      setAdmins(res.data.data || []);
    } catch {
      toast.error('Failed to load admin accounts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const handleCreate = async (e) => {
    e.preventDefault();
    setCreating(true);
    try {
      const res = await api.post('/admin/admins', form);
      setAdmins(prev => [res.data.data, ...prev]);
      setForm({ name: '', email: '' });
      setShowCreate(false);
      toast.success(`Admin account created — welcome email sent to ${form.email}`);
      if (res.data._devPassword) {
        console.info(`[DEV] Temp password for ${form.email}:`, res.data._devPassword);
        toast(`Dev mode — temp password logged to console`, { icon: '🔑', duration: 6000 });
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to create admin');
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (admin) => {
    setTogglingId(admin._id);
    try {
      const res = await api.patch(`/admin/admins/${admin._id}/status`, { isActive: !admin.isActive });
      setAdmins(prev => prev.map(a => a._id === admin._id ? res.data.data : a));
      toast.success(`${admin.name} ${!admin.isActive ? 'enabled' : 'disabled'}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to update status');
    } finally {
      setTogglingId(null);
    }
  };

  const filtered = admins.filter(a =>
    a.name.toLowerCase().includes(search.toLowerCase()) ||
    a.email.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto px-4 py-6 space-y-6">

      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-2xl bg-primary/10 flex items-center justify-center">
            <Shield className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-slate-900">Admin Accounts</h1>
            <p className="text-sm text-slate-500">{admins.length} administrator{admins.length !== 1 ? 's' : ''}</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={load} className="p-2 rounded-full hover:bg-slate-100 transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4 text-slate-500" />
          </button>
          <button
            onClick={() => setShowCreate(v => !v)}
            className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors"
          >
            <UserPlus className="w-4 h-4" />
            New Admin
          </button>
        </div>
      </div>

      {/* Create form */}
      {showCreate && (
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm p-5 space-y-4">
          <h2 className="font-semibold text-slate-800 text-sm">Create Admin Account</h2>
          <form onSubmit={handleCreate} className="space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Full Name</label>
                <input
                  type="text"
                  required
                  value={form.name}
                  onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                  placeholder="Jane Doe"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
              <div>
                <label className="block text-xs font-semibold text-slate-600 mb-1">Email Address</label>
                <input
                  type="email"
                  required
                  value={form.email}
                  onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                  placeholder="jane@clinic.com"
                  className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
              </div>
            </div>
            <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-100 rounded-xl">
              <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
              <p className="text-xs text-amber-700">
                A temporary password will be generated and sent to the email above. The new admin must change it on first login.
              </p>
            </div>
            <div className="flex gap-2 justify-end">
              <button
                type="button"
                onClick={() => { setShowCreate(false); setForm({ name: '', email: '' }); }}
                className="px-4 py-2 text-sm font-medium text-slate-600 hover:bg-slate-100 rounded-xl transition-colors"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={creating}
                className="flex items-center gap-2 px-4 py-2 bg-primary text-white rounded-xl text-sm font-semibold hover:bg-primary/90 transition-colors disabled:opacity-60"
              >
                <Mail className="w-4 h-4" />
                {creating ? 'Sending invite…' : 'Create & Send Invite'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <input
          type="text"
          placeholder="Search by name or email…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary/30"
        />
      </div>

      {/* List */}
      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
        </div>
      ) : filtered.length === 0 ? (
        <div className="text-center py-12 text-slate-400 text-sm">
          {search ? 'No admins match your search.' : 'No admin accounts yet.'}
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-outline-variant/10 shadow-sm overflow-hidden">
          {filtered.map((admin, i) => {
            const isSelf = admin._id === currentUser?.id;
            const isToggling = togglingId === admin._id;
            return (
              <div
                key={admin._id}
                className={`flex items-center gap-4 px-5 py-4 ${i < filtered.length - 1 ? 'border-b border-slate-50' : ''} ${!admin.isActive ? 'opacity-60' : ''}`}
              >
                {/* Avatar */}
                <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center shrink-0">
                  <span className="text-sm font-bold text-primary">
                    {admin.name.split(' ').map(n => n[0]).join('').slice(0, 2).toUpperCase()}
                  </span>
                </div>

                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <p className="text-sm font-semibold text-slate-900 truncate">{admin.name}</p>
                    {isSelf && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-primary/10 text-primary rounded-full uppercase tracking-wide">You</span>
                    )}
                    {admin.mustChangePassword && (
                      <span className="flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold bg-amber-100 text-amber-700 rounded-full uppercase tracking-wide">
                        <Clock className="w-2.5 h-2.5" />
                        Pending setup
                      </span>
                    )}
                    {!admin.isActive && (
                      <span className="px-1.5 py-0.5 text-[10px] font-bold bg-red-100 text-red-600 rounded-full uppercase tracking-wide">Disabled</span>
                    )}
                  </div>
                  <p className="text-xs text-slate-500 truncate">{admin.email}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Joined {formatDate(admin.createdAt)}</p>
                </div>

                {/* Status icon */}
                <div className="shrink-0">
                  {admin.isActive
                    ? <CheckCircle className="w-4 h-4 text-emerald-400" />
                    : <XCircle className="w-4 h-4 text-red-400" />
                  }
                </div>

                {/* Toggle */}
                {!isSelf && (
                  <button
                    onClick={() => handleToggle(admin)}
                    disabled={isToggling}
                    title={admin.isActive ? 'Disable account' : 'Enable account'}
                    className="shrink-0 p-1.5 rounded-lg hover:bg-slate-100 transition-colors disabled:opacity-50"
                  >
                    {admin.isActive
                      ? <ToggleRight className="w-6 h-6 text-primary" />
                      : <ToggleLeft className="w-6 h-6 text-slate-400" />
                    }
                  </button>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
